import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Fade,
  Grid,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../../../../hooks';
import {
  adminGetLessonTutorFeedbacks,
  getLessonTutorFeedbacksState,
  ILessonTutorFeedback,
  resetAdminLessonTutorFeedbackListData,
} from '../../../../../../../../../../store/V2/LessonTutorFeedbacksSlice';
import {
  adminUpdateLessonHistoryClassExtension,
  ILessonHistory,
  LESSON_HISTORY_CLASS_EXTENSION_REQUEST_STATUSES,
  TAdminUpdateLessonHistoryClassExtension,
  TLessonHistoryClassExtensionRequestStatus,
} from '../../../../../../../../../../store/V2/LessonHistorySlice';
import { ISection } from '../../../../../../../../../../store/V2/SectionSlice';
import { IUser } from '../../../../../../../../../../store/UserSlice';
import { ITutor } from '../../../../../../../../../../store/V2/TutorSlice';
import BasicLoader from '../../../../../../../../../../components/Loaders/BasicLoader';
import { TPTextField } from '../../../../../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../../../../../components/V2/Buttons';

interface ClassExtensionTabProps {
  lessonHistory: ILessonHistory;
  refreshListEnabled?: boolean;
  refreshList?: () => void;
}

type TState = {
  extendedDuration: string;
};

const classExtensionCardContainerStyles: SxProps = {
  backgroundColor: '#1E293B',
  borderRadius: '8px',
  color: '#F8FAFC',
};

const buttonStyles: SxProps = {
  padding: '6px 12px',
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '8px',
  height: '36px',
};

const initialState: TState = {
  extendedDuration: '0',
};

const CustomContainer = ({
  children,
  padding,
}: {
  children: React.ReactNode;
  padding?: string;
}) => {
  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: padding || '16px' }}>
        {children}
      </CardContent>
    </Card>
  );
};

const ClassExtensionTab: FunctionComponent<ClassExtensionTabProps> = ({
  lessonHistory,
  refreshListEnabled = false,
  refreshList,
}) => {
  const dispatch = useAppDispatch();

  // Store
  const lessonTutorFeedbacksState = useAppSelector(
    getLessonTutorFeedbacksState
  );

  // State
  const [state, setState] = useState({ ...initialState });
  const [initialLoading, setInitialLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isSubmitDisabled = !state.extendedDuration.trim().length;
  const paymentTutor = lessonHistory.paymentTutor as IUser;
  const sectionData = lessonHistory.section as ISection;
  const tutorFeedbacks =
    lessonTutorFeedbacksState.adminLessonTutorFeedbackListData &&
    lessonTutorFeedbacksState.adminLessonTutorFeedbackListData.list &&
    lessonTutorFeedbacksState.adminLessonTutorFeedbackListData.list.length
      ? lessonTutorFeedbacksState.adminLessonTutorFeedbackListData.list
      : [];

  let paymentTutorFeedback: undefined | ILessonTutorFeedback;
  let feedbackLessonExtension = 0;
  let extendedTutorAmount = 0;

  if (paymentTutor) {
    paymentTutorFeedback = tutorFeedbacks.find((item) => {
      const tutor = item.tutor as ITutor;

      if (tutor && tutor._id === paymentTutor._id) {
        return true;
      }

      return false;
    });
  }

  const isClassExtensionCardVisible = Boolean(
    paymentTutorFeedback && paymentTutorFeedback.lessonExtension
  );

  if (paymentTutorFeedback) {
    feedbackLessonExtension = paymentTutorFeedback.lessonExtension || 0;
  }

  if (lessonHistory.tutorRate && feedbackLessonExtension >= 15) {
    extendedTutorAmount = parseFloat(
      (lessonHistory.tutorRate * (feedbackLessonExtension / 60)).toFixed(2)
    );
  }

  useEffect(() => {
    initTab();

    setState((prevState) => ({
      ...prevState,
      extendedDuration: (lessonHistory.lessonExtension || 0).toString(),
    }));

    return () => {
      dispatch(resetAdminLessonTutorFeedbackListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const initTab = async () => {
    setInitialLoading(true);

    await dispatch(
      adminGetLessonTutorFeedbacks({
        limit: 200,
        offset: 0,
        section: sectionData._id,
      })
    );

    setInitialLoading(false);
  };

  const handleSubmit = async (
    status?: TLessonHistoryClassExtensionRequestStatus
  ) => {
    setIsLoading(true);

    const apiData: TAdminUpdateLessonHistoryClassExtension = {
      lessonHistoryId: lessonHistory._id,
      lessonExtensionMinutes: parseFloat(state.extendedDuration),
    };

    if (status) {
      apiData.classExtensionRequestStatus = status;
      apiData.lessonExtensionMinutes = feedbackLessonExtension;
    }

    await dispatch(adminUpdateLessonHistoryClassExtension(apiData));

    if (refreshListEnabled && refreshList) {
      refreshList();
    }

    setIsLoading(false);
  };

  const onAdjustExtension = () => {
    if (feedbackLessonExtension) {
      updateStateAttributes({
        extendedDuration: feedbackLessonExtension.toString(),
      });
    }
  };

  if (initialLoading) {
    return (
      <Fade in>
        <div style={{ padding: '30px' }}>
          <BasicLoader />
        </div>
      </Fade>
    );
  }

  return (
    <Fade in>
      <div>
        <Grid container spacing={2}>
          {isClassExtensionCardVisible ? (
            <Grid item xs={12}>
              <Card elevation={0} sx={classExtensionCardContainerStyles}>
                <CardContent style={{ padding: '16px' }}>
                  <Stack gap="8px">
                    <Typography fontSize="16px" fontWeight={600}>
                      Class extension request
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography fontSize="14px" fontWeight={400}>
                        Extended duration (minutes)
                      </Typography>
                      <Typography fontSize="14px" fontWeight={400}>
                        {feedbackLessonExtension.toFixed(2)} (
                        {feedbackLessonExtension === 1 ? 'minute' : 'minutes'})
                        +
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography fontSize="14px" fontWeight={400}>
                        Tutor Amount
                      </Typography>
                      <Typography fontSize="14px" fontWeight={400}>
                        {lessonHistory.tutorAmountCurrency}{' '}
                        {extendedTutorAmount.toFixed(2)} +
                      </Typography>
                    </Stack>
                    <Stack
                      gap="8px"
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{ pt: '8px' }}
                    >
                      {lessonHistory.classExtensionRequestStatus ? (
                        <Chip
                          size="small"
                          label={lessonHistory.classExtensionRequestStatus.toLowerCase()}
                          sx={{
                            backgroundColor: '#fff',
                            color: '#0F172A',
                            textTransform: 'capitalize',
                            fontSize: '14px',
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        <>
                          <ButtonBase
                            disabled={isLoading}
                            onClick={onAdjustExtension}
                            sx={{
                              ...buttonStyles,
                              backgroundColor: '#fff',
                              color: '#0F172A',
                            }}
                          >
                            Adjust
                          </ButtonBase>
                          <ButtonBase
                            disabled={isLoading}
                            onClick={() =>
                              handleSubmit(
                                LESSON_HISTORY_CLASS_EXTENSION_REQUEST_STATUSES.DECLINED
                              )
                            }
                            sx={{
                              ...buttonStyles,
                              backgroundColor: '#FF5630',
                              color: '#F8FAFC',
                            }}
                          >
                            Reject
                          </ButtonBase>
                          <ButtonBase
                            disabled={isLoading}
                            onClick={() =>
                              handleSubmit(
                                LESSON_HISTORY_CLASS_EXTENSION_REQUEST_STATUSES.APPROVED
                              )
                            }
                            sx={{
                              ...buttonStyles,
                              backgroundColor: '#22C55E',
                              color: '#F8FAFC',
                            }}
                          >
                            Approve
                          </ButtonBase>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <CustomContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Class extended duration (mins)
                </Typography>
                <div style={{ minWidth: '130px', maxWidth: '130px' }}>
                  <TPTextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="mins"
                    value={state.extendedDuration}
                    InputProps={{ sx: { backgroundColor: '#fff' } }}
                    onChange={(event) =>
                      updateStateAttributes({
                        extendedDuration: event.target.value,
                      })
                    }
                  />
                </div>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <CustomContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Tutor amount
                  {lessonHistory.tutorAmountCurrency
                    ? ` (${lessonHistory.tutorAmountCurrency})`
                    : null}
                </Typography>
                <div style={{ minWidth: '130px', maxWidth: '130px' }}>
                  <TPTextField
                    fullWidth
                    size="small"
                    placeholder="mins"
                    value={(lessonHistory.tutorAmount || 0).toFixed(2)}
                    InputProps={{
                      sx: { backgroundColor: '#fff' },
                      readOnly: true,
                    }}
                  />
                </div>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <DarkLoadingButton
                variant="contained"
                color="inherit"
                loading={isLoading}
                disabled={isSubmitDisabled}
                onClick={() => handleSubmit()}
                sx={{ ...defaultButtonStyles }}
              >
                Save
              </DarkLoadingButton>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <div
              style={{ minHeight: '1px', borderBottom: '1px dashed #CBD5E1' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography fontSize="16px" fontWeight={600} color="#1E293B">
              Total Summary
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <CustomContainer padding="8px 16px">
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Total rate / hour
                </Typography>
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  {lessonHistory.tutorAmountCurrency}{' '}
                  {(lessonHistory.tutorAmount || 0).toFixed(2)}
                </Typography>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <CustomContainer padding="8px 16px">
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Class Duration
                </Typography>
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  {lessonHistory.duration} Minutes
                </Typography>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <CustomContainer padding="8px 16px">
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  + Base Rate
                </Typography>
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  {lessonHistory.tutorAmountCurrency}{' '}
                  {(lessonHistory.allocatedTutorAmount || 0).toFixed(2)}
                </Typography>
              </Stack>
            </CustomContainer>
          </Grid>
          {lessonHistory.lessonExtension &&
          lessonHistory.lessonExtension >= 15 ? (
            <Grid item xs={12}>
              <div style={{ minHeight: '15px' }} />
              <CustomContainer padding="8px 16px">
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize="16px" fontWeight={500} color="#647488">
                    Extended Lesson Duration
                  </Typography>
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    {lessonHistory.lessonExtension} Minutes
                  </Typography>
                </Stack>
              </CustomContainer>
            </Grid>
          ) : null}
          {lessonHistory.lessonExtension &&
          lessonHistory.lessonExtension >= 15 &&
          lessonHistory.lessonExtensionTutorAmount ? (
            <Grid item xs={12}>
              <CustomContainer padding="8px 16px">
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize="16px" fontWeight={500} color="#647488">
                    + Extended Lesson Rate
                  </Typography>
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    {lessonHistory.tutorAmountCurrency}{' '}
                    {(lessonHistory.lessonExtensionTutorAmount || 0).toFixed(2)}
                  </Typography>
                </Stack>
              </CustomContainer>
            </Grid>
          ) : null}
          {lessonHistory.tutorPenalty ? (
            <Grid item xs={12}>
              <div style={{ minHeight: '15px' }} />
              <CustomContainer padding="8px 16px">
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize="16px" fontWeight={500} color="#647488">
                    - Penalty{' '}
                    {parseFloat(
                      lessonHistory.tutorPenalty.toFixed(2)
                    ).toString()}
                    %
                  </Typography>
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    {lessonHistory.tutorAmountCurrency}{' '}
                    {(lessonHistory.tutorPenaltyAmount || 0).toFixed(2)}
                  </Typography>
                </Stack>
              </CustomContainer>
            </Grid>
          ) : null}
          {lessonHistory.tutorPenalty && lessonHistory.tutorPenaltyReason ? (
            <Grid item xs={12}>
              <CustomContainer padding="8px 16px">
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize="16px" fontWeight={500} color="#647488">
                    Penalty Reason
                  </Typography>
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    {lessonHistory.tutorPenaltyReason}
                  </Typography>
                </Stack>
              </CustomContainer>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <div style={{ minHeight: '15px' }} />
            <CustomContainer padding="8px 16px">
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Total Amount
                </Typography>
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  {lessonHistory.tutorAmountCurrency}{' '}
                  {(lessonHistory.tutorAmount || 0).toFixed(2)}
                </Typography>
              </Stack>
            </CustomContainer>
          </Grid>
        </Grid>
      </div>
    </Fade>
  );
};

export default ClassExtensionTab;
